import { useState } from 'react'

import {
  Alert,
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import { camelCase } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspaceSeePayroll } from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceSeePayroll'

import { Fields } from '../Fields/Fields'
import { WizardData } from '../types'
import { groupFieldsByGroup } from '../utils'

type ExtendedWizardStepRenderParams = WizardStepRenderParams<WizardData> & {
  canAssignManager: boolean
}

const WorkDetailsStep = ({
  data,
  onDataChanged,
  canAssignManager,
}: ExtendedWizardStepRenderParams) => {
  const [managers, setManagers] = useState<Record<string, number | undefined>>({
    managerId: data.managerId ? +data.managerId : undefined,
    timeoffManagerId: data.timeoffManagerId ? +data.timeoffManagerId : undefined,
  })
  const fields = data.fieldsSchema?.filter((field) => field.category === 'work_details')
  const filterFields = fields?.filter(
    (field) =>
      (field.fieldName !== 'manager_id' && field.fieldName !== 'timeoff_manager_id') ||
      canAssignManager
  )
  const groupedFields = groupFieldsByGroup(filterFields)

  const handleSelect = (fieldName: string, employeeId: number | undefined) => {
    setManagers((prevManagers) => ({
      ...prevManagers,
      [fieldName]: employeeId,
    }))

    onDataChanged({
      ...data,
      managerId: fieldName === 'managerId' ? employeeId?.toString() ?? '' : data.managerId,
      timeoffManagerId:
        fieldName === 'timeoffManagerId' ? employeeId?.toString() ?? '' : data.timeoffManagerId,
    })
  }

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
  }

  const hasPayrollField = fields?.find((f) => f.fieldName === 'payroll_policy_id')

  const { canSeeFinanceWorkspacePayroll } = useFinancialWorkspaceSeePayroll()

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Text.Header2>
          {i18n.t('add_employee_modal.work_conditions_step.header', { firstName: data?.firstName })}
        </Text.Header2>
        {Object.entries(groupedFields ?? {}).map(([group, groupFields]) => (
          <Box key={group} flexDirection="row" gap="s16">
            {groupFields.map((field) => {
              return (
                <Fields
                  key={field.fieldName}
                  field={field}
                  data={data}
                  onChangeField={onChangeField}
                  handleEmployeeSelect={handleSelect}
                  employeesValue={managers[camelCase(field.fieldName)]}
                />
              )
            })}
          </Box>
        ))}
      </Box>

      <Box flexDirection="column" paddingTop="s24" maxWidth="s240">
        <Box paddingTop="s240" height="s140">
          {hasPayrollField && canSeeFinanceWorkspacePayroll && (
            <Alert color="info">{i18n.t('finance.financial_workspace.launch_info.payroll')}</Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const workDetailsStep = (canAssignManager: boolean): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.work_conditions_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <WorkDetailsStep {...props} canAssignManager={canAssignManager} />,
  isCompleted: () => true,
})
